/**
 * Requirements register tab — exposes the Requirements API on the project
 * workspace: every recorded obligation with its provenance, citation,
 * linkages, dates, and (on demand) its back-calculated schedule impact.
 *
 * Provenance is presented truthfully: DOCUMENT_CITED is evidenced,
 * INFERRED is an assumption, forecast dates are predictions. Missing
 * durations render as Unknown — never as a defaulted number.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  classifyWorkflowError,
  requirementsApi,
} from "../../services/intelligenceService";
import { canDecide, canEditSchedule } from "../../lib/roles";
import RequirementLifecyclePanel from "./RequirementLifecyclePanel";
import AddRequirementDialog from "./AddRequirementDialog";
import {
  readRegister,
  filterRequirements,
  requirementTypes,
  assumptionCount,
  countLabel,
  registerView,
  WEAK_PROVENANCE,
} from "./requirementsPresentation";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  SectionLoading,
  SectionEmpty,
  FeatureDisabled,
  SectionError,
  NoAccess,
  UnknownValue,
} from "../../components/workflow/WorkflowStates";
import {
  ProvenanceBadge,
  ConfidenceBadge,
  RequirementStatusBadge,
} from "../../components/workflow/ProvenanceBadge";
import {

  ClipboardCheck,
  ShieldAlert,
  CalendarClock,
  Link2,
  FileText,
  Quote,
  ChevronRight,
  Loader2,
  Search,
  ShieldCheck,
} from "lucide-react";

/**
 * The register's own WEAK_VERIFICATION set, mirrored exactly:
 * `frozenset({"UNVERIFIED", "INFERRED", "OPERATOR_ENTERED"})`.
 *
 * OPERATOR_ENTERED belongs here and was missing. It is what every imported
 * requirement carries, and what the register itself keys on to decide that a
 * hard gate REPORTS rather than BLOCKS. Leaving it out made a whole class of
 * obligation — the ones a beta tenant loads on day one — invisible to the
 * assumption count, the provenance filter and the "no citation" warning.
 *
 * CONFLICTING is deliberately NOT here. The register keeps it in a separate
 * REPORTED_VERIFICATION set: it is not a missing citation, it is two sources
 * disagreeing, and it has its own badge and its own resolution.
 */
const fmtDate = (value) => {
  if (!value) return null;
  try {
    return format(parseISO(value), "MMM d, yyyy");
  } catch {
    return String(value);
  }
};

const DetailField = ({ label, children }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <div className="text-sm">{children ?? <UnknownValue />}</div>
  </div>
);

const DurationField = ({ label, days }) => (
  <DetailField label={label}>
    {days === null || days === undefined ? (
      <UnknownValue reason="Not recorded. The scheduler treats this as an unresolved constraint, not zero." />
    ) : (
      `${days} day${days === 1 ? "" : "s"}`
    )}
  </DetailField>
);

/**
 * `reloadToken` changes when something outside this tab wrote to the register
 * — an import, above all. Without it a committed import is invisible: the tab
 * is already mounted, its effect keys only on `projectId`, and the operator
 * lands on a list that does not contain what they just added.
 */
const RequirementsTab = ({ projectId, user, activities = [], reloadToken = 0 }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [list, setList] = useState(null);
  const [classified, setClassified] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusGroup, setStatusGroup] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [hardGatesOnly, setHardGatesOnly] = useState(false);
  const [provenanceFilter, setProvenanceFilter] = useState("all");

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailClassified, setDetailClassified] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [impact, setImpact] = useState(null);
  const [impactClassified, setImpactClassified] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyForm, setVerifyForm] = useState({
    verificationStatus: "HUMAN_VERIFIED",
    sourceDocumentId: "",
    sourceQuote: "",
    note: "",
  });
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setClassified(null);
    try {
      const data = await requirementsApi.listForProject(projectId);
      setList(data);
    } catch (err) {
      setClassified(classifyWorkflowError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reloadToken]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (requirementId) => {
    setSelectedId(requirementId);
    setDetail(null);
    setImpact(null);
    setImpactClassified(null);
    setDetailClassified(null);
    setDetailLoading(true);
    try {
      const data = await requirementsApi.get(requirementId);
      setDetail(data);
    } catch (err) {
      setDetailClassified(classifyWorkflowError(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const loadImpact = async () => {
    if (!selectedId) return;
    setImpactLoading(true);
    setImpactClassified(null);
    try {
      const data = await requirementsApi.scheduleImpact(selectedId);
      setImpact(data);
    } catch (err) {
      setImpactClassified(classifyWorkflowError(err));
    } finally {
      setImpactLoading(false);
    }
  };

  const submitVerify = async () => {
    if (!selectedId) return;
    const body = { verificationStatus: verifyForm.verificationStatus };
    if (verifyForm.verificationStatus === "DOCUMENT_CITED") {
      if (!verifyForm.sourceDocumentId || !verifyForm.sourceQuote) {
        toast.error("A document citation needs the source document id and the quoted text.");
        return;
      }
      body.sourceDocumentId = verifyForm.sourceDocumentId;
      body.sourceQuote = verifyForm.sourceQuote;
    }
    if (verifyForm.verificationStatus === "CONFLICTING") {
      if (!verifyForm.note) {
        toast.error("Recording a conflict requires a note describing the disagreement.");
        return;
      }
    }
    if (verifyForm.note) body.note = verifyForm.note;
    setVerifying(true);
    try {
      await requirementsApi.verify(selectedId, body);
      toast.success("Verification recorded.");
      setVerifyOpen(false);
      await load();
      await openDetail(selectedId);
    } catch (err) {
      const c = classifyWorkflowError(err);
      toast.error(c.message);
    } finally {
      setVerifying(false);
    }
  };

  /**
   * `list` is only ever null before the first answer; a FAILED read is held in
   * `classified` and short-circuits below. What the register did not survive
   * was a read that SUCCEEDED with the wrong shape — `|| []` catches null and
   * undefined and nothing else, so an object or a string reached `.filter`
   * and threw during render.
   */
  const read = useMemo(() => (list === null ? null : readRegister(list)), [list]);

  const rows = useMemo(
    () =>
      filterRequirements(read?.state === "READ" ? read.items : [], {
        statusGroup,
        typeFilter,
        hardGatesOnly,
        provenanceFilter,
        search,
      }),
    [read, statusGroup, typeFilter, hardGatesOnly, provenanceFilter, search],
  );

  const types = useMemo(() => requirementTypes(read), [read]);
  const assumptions = assumptionCount(read);
  const view = registerView(read, rows);

  if (loading) return <SectionLoading label="Loading the requirements register…" />;
  if (classified?.kind === "disabled")
    return (
      <FeatureDisabled
        feature="The requirements register"
        hint="The backend API exists but OMNAS_REQUIREMENTS_API_ENABLED is off for this environment. An administrator can enable it; nothing in this tab is lost."
      />
    );
  if (classified?.kind === "no-access") return <NoAccess />;
  if (classified) return <SectionError error={classified} onRetry={load} label="The requirements register failed to load" />;


  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="bg-muted/50 border-border font-normal">
          {list.count} requirement{list.count === 1 ? "" : "s"}
        </Badge>
        {list.truncated && (
          <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-normal">
            List truncated by the server — filter to narrow it
          </Badge>
        )}
        {assumptions.established && assumptions.value > 0 && (
          <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-normal gap-1">
            <ShieldAlert className="w-3 h-3" />
            {countLabel(assumptions)} not evidenced — assumptions, not contract terms
          </Badge>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or type…"
            className="pl-8 h-8 w-56 text-sm"
          />
        </div>
        <Select value={statusGroup} onValueChange={setStatusGroup}>
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_review">In review</SelectItem>
            <SelectItem value="cleared">Cleared</SelectItem>
            <SelectItem value="inactive">Superseded / cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-44 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => (
              <SelectItem key={t} value={t}>
                {t.replaceAll("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={provenanceFilter} onValueChange={setProvenanceFilter}>
          <SelectTrigger className="h-8 w-40 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All provenance</SelectItem>
            <SelectItem value="evidenced">Evidenced / verified</SelectItem>
            <SelectItem value="assumption">Assumptions</SelectItem>
            <SelectItem value="conflict">Conflicts</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={hardGatesOnly ? "default" : "outline"}
          size="sm"
          className="h-8 text-xs"
          onClick={() => setHardGatesOnly((v) => !v)}
        >
          <ShieldAlert className="w-3.5 h-3.5 mr-1" />
          Hard gates only
        </Button>
        {canEditSchedule(user) && (
          <Button size="sm" className="h-8 text-xs ml-auto" onClick={() => setAddOpen(true)}>
            Add requirement
          </Button>
        )}
      </div>

      {/* List */}
      {view.kind !== "POPULATED" ? (
        <div data-testid="register-state" data-state={view.kind} role="status" aria-live="polite">
          <SectionEmpty
            icon={view.kind === "NO_MATCH" ? Search : ClipboardCheck}
            title={view.heading}
            hint={view.body}
          />
          {read?.unreadableRows > 0 && (
            <p className="text-xs text-amber-400 text-center pb-4" data-testid="register-unreadable-rows">
              {read.unreadableRows} row{read.unreadableRows === 1 ? "" : "s"} came back that could not be
              read, and {read.unreadableRows === 1 ? "is" : "are"} not counted above.
            </p>
          )}
        </div>
      ) : (
        <Card className="border-border">
          <CardContent className="p-0 divide-y divide-border">
            {rows.map((r) => (
              <button
                key={r.requirementId}
                type="button"
                onClick={() => openDetail(r.requirementId)}
                className="w-full text-left px-4 py-3 hover:bg-muted/30 flex items-start justify-between gap-3"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-sm">{r.title}</span>
                    {r.isHardGate && (
                      <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/25 font-normal gap-1">
                        <ShieldAlert className="w-3 h-3" />
                        Hard gate
                      </Badge>
                    )}
                    {r.isScheduleDriving && (
                      <Badge variant="outline" className="bg-blue-500/15 text-blue-400 border-blue-500/25 font-normal gap-1">
                        <CalendarClock className="w-3 h-3" />
                        Schedule-driving
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>{(r.requirementType || "").replaceAll("_", " ")}</span>
                    {r.requiredDate && <span>· Required {fmtDate(r.requiredDate)}</span>}
                    {r.forecastDate && (
                      <span
                        className="text-amber-400/90"
                        title="A prediction from review durations — not a commitment."
                      >
                        · Forecast {fmtDate(r.forecastDate)} (predicted)
                      </span>
                    )}
                    {(r.activityId || r.bidPackageId || r.procurementItemId) && (
                      <span className="inline-flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        {[
                          r.activityId && "activity",
                          r.bidPackageId && "bid package",
                          r.procurementItemId && "procurement",
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ProvenanceBadge verificationStatus={r.verificationStatus} />
                  <ConfidenceBadge level={r.evidenceConfidence} />
                  <RequirementStatusBadge status={r.status} />
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Detail sheet */}
      <Sheet open={!!selectedId} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {detailLoading ? (
            <SectionLoading label="Loading requirement…" />
          ) : detailClassified ? (
            <SectionError error={detailClassified} onRetry={() => openDetail(selectedId)} />
          ) : detail ? (
            <div className="space-y-5">
              <SheetHeader className="space-y-2 pr-8">
                <SheetTitle className="text-left">{detail.title}</SheetTitle>
                <SheetDescription className="text-left">
                  {(detail.requirementType || "").replaceAll("_", " ")}
                  {detail.requirementSubtype ? ` · ${detail.requirementSubtype}` : ""}
                </SheetDescription>
                <div className="flex flex-wrap gap-2">
                  <ProvenanceBadge verificationStatus={detail.verificationStatus} />
                  <ConfidenceBadge level={detail.evidenceConfidence} />
                  <RequirementStatusBadge status={detail.status} />
                  {detail.isHardGate && (
                    <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/25 font-normal">
                      Hard gate
                    </Badge>
                  )}
                </div>
              </SheetHeader>

              {WEAK_PROVENANCE.includes(detail.verificationStatus) && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300/90">
                  This is an assumption, not an evidenced contract requirement. Verify it
                  against a source document before relying on it commercially.
                </div>
              )}

              {detail.description && (
                <p className="text-sm text-muted-foreground">{detail.description}</p>
              )}

              {/* Citation */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-primary" />
                  Source & citation
                </h4>
                {detail.hasCitation || detail.sourceDocumentId ? (
                  <div className="rounded-lg bg-muted/30 p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <DetailField label="Document">{detail.sourceDocumentId}</DetailField>
                      <DetailField label="Revision">{detail.sourceRevision}</DetailField>
                      <DetailField label="Page">{detail.sourcePage}</DetailField>
                      <DetailField label="Section">{detail.sourceSection || detail.specSection}</DetailField>
                    </div>
                    {detail.sourceQuote && (
                      <blockquote className="border-l-2 border-primary/50 pl-3 text-sm italic text-muted-foreground flex gap-1.5">
                        <Quote className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>{detail.sourceQuote}</span>
                      </blockquote>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No citation on record. {WEAK_PROVENANCE.includes(detail.verificationStatus) ? "That is why this row is marked as an assumption." : ""}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <DetailField label="Spec section">{detail.specSection}</DetailField>
                  <DetailField label="Drawing ref">{detail.drawingReference}</DetailField>
                  <DetailField label="SOW ref">{detail.sowReference}</DetailField>
                  <DetailField label="Contract clause">{detail.contractClause}</DetailField>
                </div>
              </div>

              {/* Dates & review chain */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <CalendarClock className="w-4 h-4 text-primary" />
                  Dates & review durations
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <DetailField label="Required date">{fmtDate(detail.requiredDate)}</DetailField>
                  <DetailField label="Forecast (predicted)">
                    {detail.forecastDate ? (
                      <span className="text-amber-400/90">{fmtDate(detail.forecastDate)}</span>
                    ) : null}
                  </DetailField>
                  <DetailField label="Submitted">{fmtDate(detail.submittedDate)}</DetailField>
                  <DetailField label="Approved">{fmtDate(detail.approvedDate)}</DetailField>
                  <DurationField label="Preparation" days={detail.preparationDays} />
                  <DurationField label="Review" days={detail.reviewDays} />
                  <DurationField label="Resubmittal review" days={detail.resubmittalReviewDays} />
                  <DetailField label="Review cycles allowed">{detail.reviewCyclesAllowed}</DetailField>
                  <DetailField label="Reviewer">{detail.requiredReviewer || detail.governmentAuthority}</DetailField>
                  <DetailField label="Responsible party">{detail.responsibleParty}</DetailField>
                </div>
                {detail.blockingReason && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-300/90">
                    {detail.blockingReason}
                  </div>
                )}
              </div>

              {/* Linkage */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <Link2 className="w-4 h-4 text-primary" />
                  Linked records
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <DetailField label="Activity">{detail.activityId}</DetailField>
                  <DetailField label="Activity instance">{detail.activityInstanceId}</DetailField>
                  <DetailField label="Bid package">{detail.bidPackageId}</DetailField>
                  <DetailField label="Procurement item">{detail.procurementItemId}</DetailField>
                </div>
              </div>

              {/* Schedule impact (on demand) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Schedule impact</h4>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={loadImpact} disabled={impactLoading}>
                    {impactLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                    {impact ? "Recalculate" : "Calculate"}
                  </Button>
                </div>
                {impactClassified && (
                  <p className="text-xs text-muted-foreground">
                    {impactClassified.kind === "disabled"
                      ? "Schedule impact is not enabled in this environment."
                      : impactClassified.message}
                  </p>
                )}
                {impact && (
                  <div className="rounded-lg bg-muted/30 p-3 space-y-2 text-sm">
                    {impact.classification && (
                      <DetailField label="Classification">
                        {(impact.classification.classification || "").replaceAll("_", " ")}
                        {impact.classification.reasoning ? ` — ${impact.classification.reasoning}` : ""}
                      </DetailField>
                    )}
                    {impact.dateChain && Object.keys(impact.dateChain).length > 0 ? (
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(impact.dateChain).map(([k, v]) => (
                          <DetailField key={k} label={k.replace(/([A-Z])/g, " $1").toLowerCase()}>
                            {typeof v === "string" ? fmtDate(v) || v : v === null ? null : JSON.stringify(v)}
                          </DetailField>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        The date chain did not resolve — a required duration is unknown. The
                        register reports the gap instead of assuming an industry default.
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground">
                      Proposed calculation — requires operator confirmation before anything acts on it.
                    </p>
                  </div>
                )}
              </div>

              {detail.conditions && (
                <DetailField label="Conditions">{detail.conditions}</DetailField>
              )}
              {detail.notes && <DetailField label="Notes">{detail.notes}</DetailField>}

              {/* Submit / review lifecycle — backend state machine only */}
              <RequirementLifecyclePanel
                requirement={detail}
                user={user}
                onChanged={async () => {
                  await load();
                  await openDetail(selectedId);
                }}
              />

              {/* Verify action */}
              {canDecide(user) && !["SUPERSEDED", "CANCELLED"].includes(detail.status) && (
                <div className="pt-2 border-t border-border">
                  <Button size="sm" onClick={() => setVerifyOpen(true)}>
                    <ShieldCheck className="w-4 h-4 mr-1.5" />
                    Record verification
                  </Button>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Raising verification requires evidence: a citation needs the document and
                    quoted text; a conflict needs a note.
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Manual add — operator-entered, honestly labeled */}
      <AddRequirementDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={projectId}
        activities={activities}
        onCreated={load}
      />

      {/* Verify dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record verification</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">New verification status</Label>
              <Select
                value={verifyForm.verificationStatus}
                onValueChange={(v) => setVerifyForm((f) => ({ ...f, verificationStatus: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DOCUMENT_CITED">Document cited (evidenced)</SelectItem>
                  <SelectItem value="HUMAN_VERIFIED">Human verified</SelectItem>
                  <SelectItem value="CONFLICTING">Conflicting sources</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {verifyForm.verificationStatus === "DOCUMENT_CITED" && (
              <>
                <div>
                  <Label className="text-xs">Source document id</Label>
                  <Input
                    className="mt-1"
                    value={verifyForm.sourceDocumentId}
                    onChange={(e) => setVerifyForm((f) => ({ ...f, sourceDocumentId: e.target.value }))}
                    placeholder="Document id from the Documents tab"
                  />
                </div>
                <div>
                  <Label className="text-xs">Quoted text</Label>
                  <Textarea
                    className="mt-1"
                    rows={3}
                    value={verifyForm.sourceQuote}
                    onChange={(e) => setVerifyForm((f) => ({ ...f, sourceQuote: e.target.value }))}
                    placeholder="Exact language from the document that states this requirement"
                  />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs">
                Note{verifyForm.verificationStatus === "CONFLICTING" ? " (required — describe the disagreement)" : " (optional)"}
              </Label>
              <Textarea
                className="mt-1"
                rows={2}
                value={verifyForm.note}
                onChange={(e) => setVerifyForm((f) => ({ ...f, note: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setVerifyOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={submitVerify} disabled={verifying}>
                {verifying && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                Confirm & record
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RequirementsTab;
